import { jwtDecode, JwtPayload } from "jwt-decode";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface UserT {
  created_at: string;
  email: string;
  first_name: string;
  last_name: string;
  id: number;
  token: string;
  updated_at: string;
}
type JwtPayload_T = JwtPayload & {
  picture?: string;
};
export interface AuthContextType {
  isAuthenticated: boolean;
  login: (googleLoginCredential: string) => void;
  logout: () => void;
  setLastAttemptedRoute: (route: string) => void;
  lastAttemptedRoute: string;
  authAttributes: UserT | null;
  loading: boolean;
  userAuthToken: string;
  userProfilePic?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [userAuthToken, setUserAuthToken] = useState<string>("");
  const [userProfilePic, setUserProfilePic] = useState<string>("");
  const [lastAttemptedRoute, setLastAttemptedRoute] = useState<string>("/");
  const [authAttributes, setAuthAttributes] = useState<UserT | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const GANESH_API_URL = process.env.REACT_APP_API_URL;

  // Store last attempted route when accessing protected pages
  useEffect(() => {
    if (!isAuthenticated && location.pathname !== "/login") {
      const fullUrl = window.location.href;
      localStorage.setItem("ganesh-last-route", fullUrl);
    }
  }, [location.pathname, isAuthenticated]);

  // Check for a valid user session
  useEffect(() => {
    const storedRoute = localStorage.getItem("ganesh-last-route");
    if (storedRoute) {
      const url = new URL(storedRoute, window.location.origin);
      const lastRoute = url.pathname + url.search + url.hash;
      if (lastRoute) {
        setLastAttemptedRoute(lastRoute);
      }
    }
    authenticateUser();
  }, []);

  const authenticateUser = async () => {
    const storedUser = localStorage.getItem("dataplor-user") || "{}";
    const ganesh_token = localStorage.getItem("ganesh-token") || "";
    const user_profile_pic = localStorage.getItem("user-profile-pic") || "";
    const user = JSON.parse(storedUser) as UserT;

    // Get the last attempted route directly from localStorage to avoid state timing issues
    const storedRoute = localStorage.getItem("ganesh-last-route");
    let lastRoute = "/";
    if (storedRoute) {
      const url = new URL(storedRoute, window.location.origin);
      lastRoute = url.pathname + url.search + url.hash;
    }

    if (user && ganesh_token) {
      // Check if backend has Dataplor credentials stored
      try {
        const response = await fetch(
          `${GANESH_API_URL}/check_dataplor_credentials`,
          {
            method: "GET",
            headers: {
              Authorization: ganesh_token,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 401) {
          // Credentials missing, force re-authentication
          console.log(
            "Dataplor credentials not found, forcing re-authentication"
          );
          logout();
          return;
        }

        if (!response.ok) {
          console.error("Failed to check credentials:", response.statusText);
          // On error, proceed with existing authentication but warn user
          console.warn(
            "Could not verify Dataplor credentials, some features may not work"
          );
        }
      } catch (error) {
        console.error("Error checking Dataplor credentials:", error);
        // On network error, proceed with existing authentication but warn user
        console.warn(
          "Could not verify Dataplor credentials, some features may not work"
        );
      }

      setAuthAttributes(user);
      setUserAuthToken(ganesh_token);
      setUserProfilePic(user_profile_pic);
      setIsAuthenticated(true);

      // Navigate to the last attempted route using the value from localStorage
      if (lastRoute !== "/login" && lastRoute !== "/") {
        navigate(lastRoute, { replace: true });
      }
    } else {
      setIsAuthenticated(false);
    }

    // Set loading to false after authentication is complete
    setLoading(false);
  };

  const login = async (googleLoginCredential: string) => {
    try {
      const payload = {
        authorization: {
          jwt: googleLoginCredential,
        },
      };

      const response = await fetch(`${GANESH_API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.log(response);
        throw new Error("Login request failed.");
      }

      const data = await response.json();
      const user_attributes = data.attributes; // user attributes

      localStorage.setItem("dataplor-user", JSON.stringify(user_attributes));

      const ganesh_token = data.ganesh_token; // ganesh api token

      localStorage.setItem("ganesh-token", ganesh_token);

      const decoded: JwtPayload_T = jwtDecode(googleLoginCredential);
      if (decoded && decoded.picture) {
        localStorage.setItem("user-profile-pic", decoded.picture); // profile pic
      }

      authenticateUser();
    } catch (error) {
      console.error("Login failed:", error);
      logout();
    }
  };

  const logout = () => {
    localStorage.removeItem("dataplor-user");
    localStorage.removeItem("ganesh-token");
    localStorage.removeItem("ganesh-last-route");
    localStorage.removeItem("user-profile-pic");
    setIsAuthenticated(false);
    setUserProfilePic("");
    setAuthAttributes(null);
    setUserAuthToken("");
    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        setLastAttemptedRoute,
        lastAttemptedRoute,
        authAttributes,
        userAuthToken,
        loading,
        userProfilePic,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
